/**
 * Deterministic dex_id → region lookup (Build 13a).
 *
 * Replaces the old hue/header region *detector* — the per-frame path that
 * read the Pokédex header text (or screen tint), fuzzy-matched it to a
 * region, applied a confidence threshold, and fell back to "unknown" when
 * nothing matched. That detector was the source of the b12.3 misrouting
 * (22 frames → "unknown", 6 → Hisui, 5 → Johto, 2 → Paldea): the header
 * read is noisy, but the dex *number* read is high-confidence, and the
 * National Dex number alone determines the region. So we derive the region
 * deterministically from the trusted dex id and never guess from chrome.
 *
 * The boundaries below match Pokémon GO's OWN Pokédex sorting, which treats
 * two stretches as their own regions rather than folding them into a
 * neighbour:
 *   - 808-809 (Meltan / Melmetal) → "unidentified" (Niantic groups these
 *     two as a standalone bucket, NOT part of Alola).
 *   - 899-905 (the Hisui evolution line) → "hisui" (separate from Galar,
 *     which PoGo sorts as 810-898).
 *
 * This function is PURE and total: every integer maps to exactly one
 * bucket, including out-of-range ids (→ "unknown"). That makes it trivially
 * unit-testable on every boundary — see dexIdToRegion.test.ts.
 */

/**
 * Canonical OCR-pipeline region buckets. A superset of the UI region
 * taxonomy in types.ts: it adds "unidentified" (Meltan/Melmetal) because
 * Pokémon GO sorts that pair separately, and "unknown" for out-of-roster
 * misreads.
 */
export type OcrRegion =
  | "kanto"
  | "johto"
  | "hoenn"
  | "sinnoh"
  | "unova"
  | "kalos"
  | "alola"
  | "unidentified"
  | "galar"
  | "hisui"
  | "paldea"
  | "unknown";

/**
 * Inclusive [min, max] dex-id boundaries per region, in ascending order and
 * matching Pokémon GO's Pokédex sorting exactly. Hisui (899-905) and
 * Unidentified (808-809) are deliberately their own rows, NOT merged into
 * Galar or Alola.
 */
const REGION_RANGES: ReadonlyArray<{ region: OcrRegion; min: number; max: number }> = [
  { region: "kanto", min: 1, max: 151 },
  { region: "johto", min: 152, max: 251 },
  { region: "hoenn", min: 252, max: 386 },
  { region: "sinnoh", min: 387, max: 493 },
  { region: "unova", min: 494, max: 649 },
  { region: "kalos", min: 650, max: 721 },
  { region: "alola", min: 722, max: 807 },
  { region: "unidentified", min: 808, max: 809 },
  { region: "galar", min: 810, max: 898 },
  { region: "hisui", min: 899, max: 905 },
  { region: "paldea", min: 906, max: 1025 },
];

/**
 * Map a National Dex id to its Pokémon GO region. Total and deterministic:
 * non-integer or out-of-roster ids (≤0, >1025, NaN) map to "unknown" so the
 * caller can skip them without a separate validity check.
 */
export function dexIdToRegion(id: number): OcrRegion {
  if (!Number.isInteger(id)) return "unknown";
  for (const range of REGION_RANGES) {
    if (id >= range.min && id <= range.max) return range.region;
  }
  return "unknown";
}

/**
 * Title-case display label for a region, for user-facing strings such as the
 * live-scan PiP commit message ("+4 to Hoenn / Perfect (252,253,254,255)").
 */
export function dexRegionLabel(region: OcrRegion): string {
  if (region === "unknown") return "Unknown";
  return region.charAt(0).toUpperCase() + region.slice(1);
}
